import { computed, observable, runInAction } from "mobx"


import { getLineGutterWidth } from "@/lib/diff/codeRenderingCommon.ts"
import { getInlineDiffCollapsedLinesRange } from "@/lib/diff/diffCollapse.ts"
import { range } from "@/lib/diff/misc.ts"
import { Model } from "@/lib/Model.ts"
import { isNotNull, isNullish } from "@/lib/std.ts"
import { NavigationBarWidth, NavigationBarWidthMobile } from "@/routes/auth/NavigationBar.tsx"

import type { DiffExpandSide, DiffPart } from "@/components/Diff/diffTypes.ts"
import type { InlineDiff, InlineDiffLine } from "@/lib/client/generated/TypeDefinitions.ts"
import type { IntRange } from "@/lib/diff/misc.ts"
import type { Property } from "csstype"
import type { IObservableValue } from "mobx"


export class DiffModel extends Model {
  private readonly expandedLines: IObservableValue<IntRange[]> = observable.box([])

  readonly parts: IObservableValue<DiffPart[]>
  readonly gutterWidth: IObservableValue<Property.Width>
  readonly width: IObservableValue<Property.Width>

  constructor(
    private readonly diff: InlineDiff,
    private readonly isMobile: boolean,
    panelWidth?: () => Property.Width,
    gutterWidth?: IObservableValue<Property.Width>
  ) {
    super("DiffModel")

    this.parts = computed(this.buildParts)

    this.gutterWidth = computed(() => {
      return gutterWidth?.get() || getLineGutterWidth(diff)
    })

    this.width = observable.box(this.getDiffWidth(panelWidth))
  }

  element: HTMLDivElement | null = null

  readonly refCallback = (element: HTMLDivElement | null) => {
    if (!element || element === this.element) return

    this.element = element

    const resizeObserver = new ResizeObserver(() => {
      runInAction(() => {
        this.width.set(this.getDiffWidth())
      })
    })

    if (this.element) {
      [document.body, ...document.querySelectorAll("[data-resizable-container]")].forEach(it => {
        resizeObserver.observe(it)
      })
      this.lifetime.add(() => resizeObserver.disconnect(), "DiffModel:resizeObserver")
    }
  }

  private getDiffWidth(panelWidth?: () => Property.Width) {
    if (panelWidth !== undefined) {
      return panelWidth()
    } else {
      const container = this.element?.closest("[data-resizable-container]") as HTMLElement

      if (container) {
        const leftOffset = this.isMobile ? NavigationBarWidthMobile : NavigationBarWidth
        const diffPanelPercentage = (container.getAttribute("data-resizable-container") || "100%")
        const diffPanelWidth = `calc(${window.innerWidth - leftOffset}px / 100 * ${parseFloat(diffPanelPercentage)})`

        const containerPadding = "(24px * 2)"
        const containerBorder = "(1px * 2)"

        return `calc(${diffPanelWidth} - ${containerPadding} - ${containerBorder})`
      } else {
        return "auto"
      }
    }
  }

  private readonly buildParts = () => {
    const collapsedRanges = getInlineDiffCollapsedLinesRange(this.diff, this.expandedLines.get())

    const expand = (range: IntRange) => {
      return (side: DiffExpandSide) => {
        const expandedLinesList = this.getExpandedLinesByStep(range, side)

        runInAction(() => {
          this.expandedLines.set(this.expandedLines.get().concat(expandedLinesList))
        })
      }
    }

    let lastCollapsedIndicesRange: IntRange | null = null
    let lastCollapsedNewNumbersRange: IntRange | null = null
    let lastCollapsedOldNumbersRange: IntRange | null = null

    const parts: DiffPart[] = []
    this.diff.lines.forEach((line, index) => {
      if (collapsedRanges.some(it => it.contains(index))) {
        const rangeStartIndex = isNullish(lastCollapsedIndicesRange?.start) ? index : lastCollapsedIndicesRange.start
        lastCollapsedIndicesRange = range(rangeStartIndex, index)

        if (isNotNull(line.newLineNum)) {
          const rangeStartNumber = lastCollapsedNewNumbersRange?.start || line.newLineNum
          lastCollapsedNewNumbersRange = range(rangeStartNumber, line.newLineNum)
        }

        if (isNotNull(line.oldLineNum)) {
          const rangeStartNumber = lastCollapsedOldNumbersRange?.start || line.oldLineNum
          lastCollapsedOldNumbersRange = range(rangeStartNumber, line.oldLineNum)
        }

        return
      }

      if (lastCollapsedIndicesRange) {
        parts.push(diffHunk(lastCollapsedIndicesRange, expand(lastCollapsedIndicesRange)))
      }

      lastCollapsedIndicesRange = null
      lastCollapsedNewNumbersRange = null
      lastCollapsedOldNumbersRange = null

      parts.push(diffLine(line))
    })

    if (lastCollapsedIndicesRange) {
      parts.push(diffHunk(lastCollapsedIndicesRange, expand(lastCollapsedIndicesRange)))
    }

    return parts
  }

  private getExpandedLinesByStep(expandedLines: IntRange, expandSide: DiffExpandSide, expandStep = 10): IntRange[] {
    if (expandedLines.endInclusive - expandedLines.start + 1 <= expandStep) {
      return [expandedLines]
    }

    if (expandSide === "top") {
      return [range(expandedLines.start, expandedLines.start + expandStep)]
    }

    if (expandSide === "bottom") {
      return [range(expandedLines.endInclusive - expandStep, expandedLines.endInclusive)]
    }

    return [expandedLines]
  }
}

function diffHunk(range: IntRange, expand: (side: DiffExpandSide) => void): DiffPart {
  return {
    type: "hunk",

    key: `hunk-${range.start}-${range.endInclusive}`,
    data: range,
    expand,
  }
}

function diffLine(data: InlineDiffLine): DiffPart {
  return {
    type: "line",

    key: `line-${data.newLineNum}-${data.oldLineNum}`,
    data,
  }
}